import React from 'react';
import {
  Braces,
  Check,
  Cloud,
  Database,
  FileSearch,
  FileText,
  LayoutDashboard,
  Map,
  Plug,
  Sparkles,
  Timer,
  Zap,
} from 'lucide-react';
import { COPY } from '../../i18n/copy';
import { useI18n } from '../../i18n/useI18n';

const SOURCE_ICONS = [FileText, Database, Braces, Cloud, FileSearch, Plug] as const;
const OUTPUT_ICONS = [LayoutDashboard, Sparkles, Map] as const;
const CAPABILITY_ICONS = [Database, Sparkles, Check] as const;

const EricaConnectors: React.FC = () => {
  const { lang } = useI18n();
  const c = COPY[lang];

  return (
    <section id="connectors" className="bg-slate-50 py-16 sm:py-20">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-3xl text-center">
          <h2 className="text-3xl font-semibold tracking-tight text-slate-900 sm:text-4xl">
            {c.connectors.title}
          </h2>
          <p className="mt-4 text-lg text-slate-600">{c.connectors.subtitle}</p>
        </div>

        <div className="mt-12 grid items-start gap-10 lg:grid-cols-2">
          <div className="space-y-8">
            <ul className="space-y-3">
              {c.connectors.bullets.map((bullet) => (
                <li key={bullet} className="flex gap-3 text-sm text-slate-700">
                  <span className="mt-0.5 inline-flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-white text-slate-900 shadow-sm ring-1 ring-slate-200">
                    <Check className="h-3.5 w-3.5" />
                  </span>
                  <span className="leading-relaxed">{bullet}</span>
                </li>
              ))}
            </ul>

            <div>
              <div className="text-sm font-semibold text-slate-900">{c.connectors.capabilityTitle}</div>
              <div className="mt-4 grid gap-3 sm:grid-cols-3">
                {c.connectors.capabilities.map((item, idx) => {
                  const Icon = CAPABILITY_ICONS[idx] ?? Sparkles;
                  return (
                    <div
                      key={item.title}
                      className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm"
                    >
                      <div className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-cyan-50 text-cyan-700">
                        <Icon className="h-4 w-4" />
                      </div>
                      <div className="mt-3 text-sm font-semibold text-slate-900">{item.title}</div>
                      <div className="mt-1 text-xs leading-relaxed text-slate-600">{item.description}</div>
                    </div>
                  );
                })}
              </div>
            </div>

            
          </div>

          <div className="relative">
            <div className="pointer-events-none absolute inset-0 -z-10 rounded-[36px] bg-gradient-to-br from-slate-200/70 via-white to-white blur-2xl opacity-80" />
            <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                {c.connectors.sources.slice(0, SOURCE_ICONS.length).map((label, idx) => {
                  const Icon = SOURCE_ICONS[idx] ?? Plug;
                  return (
                    <div
                      key={label}
                      className="flex items-center gap-3 rounded-xl border border-slate-200 bg-slate-50 px-3 py-3"
                    >
                      <Icon className="h-5 w-5 text-slate-900" />
                      <span className="text-xs font-semibold text-slate-700">{label}</span>
                    </div>
                  );
                })}
              </div>

              <div className="mt-6 flex items-center justify-center">
                <div className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-cyan-600 to-blue-600 px-4 py-2 text-sm font-semibold text-white">
                  <Sparkles className="h-4 w-4" />
                  <span>ERICA</span>
                </div>
              </div>

              <div className="mt-6 grid grid-cols-3 gap-3">
                {c.connectors.outputs.slice(0, OUTPUT_ICONS.length).map((label, idx) => {
                  const Icon = OUTPUT_ICONS[idx] ?? LayoutDashboard;
                  return (
                    <div
                      key={label}
                      className="flex flex-col items-center justify-center rounded-xl border border-slate-200 bg-white px-3 py-4 text-center"
                    >
                      <Icon className="h-5 w-5 text-slate-900" />
                      <div className="mt-2 text-xs font-semibold text-slate-700">{label}</div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default EricaConnectors;
